import { useRef, useState } from 'react';
import { FileText, Upload, X, Type } from 'lucide-react';
import { extractTextFromFile } from '../../services/salesDemoService';
import { Tag } from '../ui/atoms/Tag';
import { Button } from '@/components/ui/atoms/Button';

interface JobContextCardProps {
  jobDescription: string;
  onJobDescriptionChange: (text: string) => void;
  disabled?: boolean;
}

type InputMode = 'choose' | 'paste' | 'upload';

export function JobContextCard({
  jobDescription,
  onJobDescriptionChange,
  disabled = false,
}: JobContextCardProps) {
  const [mode, setMode] = useState<InputMode>(jobDescription ? 'paste' : 'choose');
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (file: File) => {
    setUploading(true);
    setUploadError(null);
    setFileName(file.name);

    try {
      const text = await extractTextFromFile(file);
      onJobDescriptionChange(text);
      setMode('paste');
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
      setFileName(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) void handleFileUpload(file);
  };

  const handleClear = () => {
    onJobDescriptionChange('');
    setFileName(null);
    setMode('choose');
  };

  // Shared hidden file input
  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept=".pdf,.docx,.doc"
      className="hidden"
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (file) void handleFileUpload(file);
      }}
    />
  );

  // Choose mode
  if (mode === 'choose' && !jobDescription) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 h-full flex flex-col">
        <div className="p-5 flex flex-col flex-1">
          <div className="mb-1">
            <h2 className="font-heading text-dark-invirtus text-base font-bold">
              Job Description
            </h2>
            <p className="font-body text-dark-invirtus/35 text-xs mt-0.5">
              Provide the role details to tailor the interview
            </p>
          </div>

          <div className="flex-1 flex flex-col gap-2.5 justify-center py-4">
            <button
              onClick={() => setMode('paste')}
              disabled={disabled}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl border border-gray-150 hover:border-[var(--color-purple)] hover:bg-[var(--color-light-purple)]/20 transition-all duration-200 text-left group"
            >
              <div className="w-9 h-9 rounded-lg bg-[var(--color-light-purple)]/50 flex items-center justify-center shrink-0 group-hover:bg-[var(--color-light-purple)]  transition-colors">
                <Type className="w-4 h-4 text-[var(--color-purple)]" />
              </div>
              <div className="min-w-0">
                <p className="font-heading text-dark-invirtus text-sm font-semibold leading-tight">
                  Paste Text
                </p>
                <p className="font-body text-dark-invirtus/35 text-[11px] leading-tight mt-0.5">
                  Type or paste job description
                </p>
              </div>
            </button>

            <button
              onClick={() => {
                setMode('upload');
                setTimeout(() => fileInputRef.current?.click(), 100);
              }}
              disabled={disabled}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl border border-gray-150 hover:border-[var(--color-purple)] hover:bg-[var(--color-light-purple)]/20 transition-all duration-200 text-left group"
            >
              <div className="w-9 h-9 rounded-lg bg-[var(--color-light-purple)]/50 flex items-center justify-center shrink-0 group-hover:bg-[var(--color-light-purple)] transition-colors">
                <Upload className="w-4 h-4 text-[var(--color-purple)]" />
              </div>
              <div className="min-w-0">
                <p className="font-heading text-dark-invirtus text-sm font-semibold leading-tight">
                  Upload File
                </p>
                <p className="font-body text-dark-invirtus/35 text-[11px] leading-tight mt-0.5">
                  PDF or DOCX document
                </p>
              </div>
            </button>
          </div>
        </div>
        {fileInput}
      </div>
    );
  }

  // Upload mode
  if (mode === 'upload' && !jobDescription) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 h-full flex flex-col">
        <div className="p-5 flex flex-col flex-1">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-dark-invirtus text-base font-bold">
              Upload
            </h2>
            <Button
              type="icon"
              intent="muted"
              size="xs"
              onClick={() => setMode('choose')}
              aria-label="Close upload"
              icon={<X />}
            />
          </div>

          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            className="flex-1 flex flex-col items-center justify-center gap-2.5 border border-dashed border-gray-200 rounded-xl hover:border-[var(--color-purple)]/50 transition-colors cursor-pointer"
            onClick={() => !uploading && fileInputRef.current?.click()}
          >
            {uploading ? (
              <>
                <div className="w-6 h-6 border-2 border-[var(--color-purple)] border-t-transparent rounded-full animate-spin" />
                <p className="font-body text-dark-invirtus/40 text-xs">
                  Extracting text...
                </p>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-xl bg-[var(--color-light-purple)]/30 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[var(--color-purple)]/60" />
                </div>
                <div className="text-center">
                  <p className="font-body text-dark-invirtus/50 text-xs">
                    Drop file here or <span className="text-[var(--color-purple)] font-semibold">browse</span>
                  </p>
                  <p className="font-body text-dark-invirtus/25 text-[10px] mt-0.5">
                    PDF, DOCX
                  </p>
                </div>
              </>
            )}
            {uploadError && (
              <p className="text-[var(--color-red)] text-[11px]">{uploadError}</p>
            )}
          </div>
        </div>
        {fileInput}
      </div>
    );
  }

  // Paste mode
  return (
    <div className="bg-white rounded-2xl border border-gray-100 h-full flex flex-col">
      <div className="p-5 flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <h2 className="font-heading text-dark-invirtus text-base font-bold">
              Job Description
            </h2>
            {fileName && (
              <Tag intent="primary" variant="subtle" icon={<FileText className="w-2.5 h-2.5" />}>
                {fileName}
              </Tag>
            )}
          </div>
          {!disabled && jobDescription && (
            <Button
              type="icon"
              intent="muted"
              size="xs"
              onClick={handleClear}
              aria-label="Clear job description"
              icon={<X />}
            />
          )}
        </div>
        <textarea
          className="flex-1 w-full resize-none rounded-xl border border-gray-100 bg-[var(--color-background-gray)] p-3.5 font-body text-sm text-dark-invirtus placeholder:text-dark-invirtus/25 focus:outline-none focus:ring-1 focus:ring-[var(--color-purple)]/30 focus:border-[var(--color-purple)]/30 transition-all leading-relaxed"
          placeholder="Paste job description here..."
          value={jobDescription}
          onChange={(e) => onJobDescriptionChange(e.target.value)}
          readOnly={disabled}
        />
        {!disabled && !jobDescription && (
          <Button
            type="text"
            intent="muted"
            size="xs"
            onClick={() => { setMode('upload'); setTimeout(() => fileInputRef.current?.click(), 100); }}
            icon={<Upload />}
            label="Or upload a file"
            className="self-start mt-2 !px-0"
          />
        )}
      </div>
      {fileInput}
    </div>
  );
}
